import { type ButtonHTMLAttributes, forwardRef } from 'react';

import clsx from 'clsx';

import { Icon } from '@/components/icons/Icon';

const defaultButtonStyling =
  'relative flex items-center gap-2 cursor-pointer border rounded py-2 px-5 text-sx md:text-sm md:font-medium font-medium hover:outline-none focus:outline-none disabled:cursor-not-allowed';

export const variants = {
  primary: clsx(
    defaultButtonStyling,
    'border-transparent bg-turquoise-500 text-black transition-colors duration-150 ease-in-out hover:bg-turquoise-700 hover:fill-white disabled:bg-gray-50 disabled:text-gray-800 disabled:hover:bg-gray-200',
  ),
  secondary: clsx(
    defaultButtonStyling,
    'border-solid border-gray-400 bg-white text-black transition-colors duration-150 ease-in-out hover:border-gray-800 hover:bg-gray-50 hover:fill-white disabled:text-gray-800 disabled:hover:border-gray-200 disabled:hover:bg-white',
  ),
  danger: clsx(
    defaultButtonStyling,
    'border-transparent bg-red-500 text-white transition-colors duration-150 ease-in-out hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-800 disabled:hover:bg-gray-200',
  ),
  link: clsx(
    defaultButtonStyling,
    'border-transparent bg-transparent text-black transition-colors duration-150 ease-in-out hover:bg-gray-50 disabled:bg-transparent disabled:text-gray-800 disabled:hover:bg-transparent',
  ),
  none: '',
};

export interface IButton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  ariaLabel?: string;
  isLoading?: boolean;
  block?: boolean;
  dataTestId?: string;
}

export const Button = forwardRef<HTMLButtonElement, IButton>(
  (
    {
      type = 'button',
      variant = 'primary',
      disabled = false,
      role = 'button',
      block = false,
      isLoading = false,
      className,
      ariaLabel,
      onClick,
      children,
      dataTestId,
      ...props
    }: IButton,
    ref,
  ) => (
    <button
      type={type}
      role={role}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={clsx(variants[variant], className, block && 'w-full justify-center')}
      disabled={disabled}
      onClick={onClick}
      ref={ref}
      {...props}
    >
      {isLoading && (
        <Icon
          icon="spinner"
          iconClass={`${
            variant === 'primary'
              ? disabled
                ? 'stroke-gray-800'
                : 'stroke-black'
              : variant === 'danger'
              ? 'stroke-white'
              : ''
          }`}
        />
      )}
      {children}
    </button>
  ),
);

Button.displayName = 'Button';
